import { Link, useParams } from 'react-router-dom'
import { Badge, Button, Card, Divider, InlineError, Skeleton } from '../../components/ui'
import { useTenant } from '../../tenant/useTenant'
import { useGetMyBookingQuery } from '../../services/accountApi'
import { formatMoney } from '../../utils/format'
import { PaymentSummary, ServiceLines } from './BookingBill'
import { bookingWhen, cancelPolicyNote, statusLabel, statusTone } from './bookingDisplay'

/** Statuses of a booking the salon has not started on yet. */
const SCHEDULED = ['pending', 'confirmed']

/** I6 — one appointment in full. */
export function BookingDetailPage() {
  const { slug, bootstrap } = useTenant()
  const { id } = useParams<{ id: string }>()
  const bookingId = Number(id)

  const { data, isLoading, isError, error, refetch } = useGetMyBookingQuery(
    { slug, id: bookingId },
    { skip: !Number.isFinite(bookingId) || bookingId <= 0 },
  )

  const cancelAllowed = bootstrap.flags.customer_cancel_allowed
  const policy = cancelPolicyNote(bootstrap.flags.cancel_cutoff_hours)
  // Each appointment of the visit with its own bill; only sent by the account endpoints.
  const parts = (data?.bookings ?? []).filter((part) => part.services && part.summary)

  return (
    <div className="container-page py-section-gap-md md:py-section-gap-lg">
      <Link
        to={`/${slug}/account`}
        className="mb-12 inline-flex items-center gap-2 font-label-caps text-label-caps uppercase text-on-surface-variant transition-colors hover:text-primary"
      >
        <span className="material-symbols-outlined text-[18px]">arrow_back</span>
        My appointments
      </Link>

      {isLoading && (
        <Card variant="filled">
          <Skeleton lines={5} />
        </Card>
      )}

      {isError && <InlineError error={error} onRetry={() => void refetch()} />}

      {data && (
        <div className="mx-auto max-w-3xl">
          <header className="mb-12">
            <div className="mb-3 flex items-center gap-3">
              <Badge tone={statusTone(data.status)}>{statusLabel(data.status)}</Badge>
              <span className="font-body-md text-sm text-on-surface-variant">Booking reference {data.code}</span>
            </div>
            <h1 className="font-display text-headline-md text-primary">{bookingWhen(data)}</h1>
            <p className="mt-2 flex items-center gap-1.5 font-body-md text-body-md text-on-surface-variant">
              <span className="material-symbols-outlined text-[18px]">location_on</span>
              {data.branch.name}
            </p>
          </header>

          {parts.length > 1 ? (
            // A visit with several specialists is one appointment per specialist at the
            // salon, each with its own bill - shown here exactly as the salon sees them.
            <div className="flex flex-col gap-8">
              {parts.map((part) => (
                <section key={part.id} className="rounded-xl border border-primary/10 bg-surface p-8">
                  <div className="mb-8 flex flex-wrap items-center justify-between gap-3 border-b border-primary/10 pb-4">
                    <h2 className="font-label-caps text-label-caps uppercase text-primary">
                      {part.staff_name ?? 'Services'}
                    </h2>
                    <div className="flex items-center gap-3">
                      {part.status && part.status !== data.status && (
                        <Badge tone={statusTone(part.status)}>{statusLabel(part.status)}</Badge>
                      )}
                      <span className="font-body-md text-sm text-on-surface-variant">{part.code}</span>
                    </div>
                  </div>
                  <ServiceLines services={part.services ?? []} currency={bootstrap.currency} />
                  {part.summary && (
                    <>
                      <Divider className="my-6" />
                      <PaymentSummary bill={part.summary} currency={bootstrap.currency} />
                    </>
                  )}
                </section>
              ))}

              <div className="flex items-center justify-between rounded-xl border border-primary/10 bg-surface p-8">
                <span className="font-label-caps text-label-caps uppercase text-on-surface-variant">Visit total</span>
                <span className="font-display text-headline-sm text-primary">
                  {formatMoney(data.total, bootstrap.currency)}
                </span>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-primary/10 bg-surface p-8">
              <h2 className="mb-8 border-b border-primary/10 pb-4 font-label-caps text-label-caps uppercase text-primary">
                Services
              </h2>
              <ServiceLines services={data.services} currency={bootstrap.currency} />

              <Divider className="my-6" />

              {data.summary ? (
                <PaymentSummary bill={data.summary} currency={bootstrap.currency} />
              ) : (
                <div className="flex items-center justify-between">
                  <span className="font-label-caps text-label-caps uppercase text-on-surface-variant">Total</span>
                  <span className="font-display text-headline-sm text-primary">
                    {formatMoney(data.total, bootstrap.currency)}
                  </span>
                </div>
              )}
            </div>
          )}

          {data.can_reschedule && (
            <div className="mt-8">
              <Button to={`/${slug}/account/bookings/${data.id}/reschedule`} size="lg">
                Reschedule appointment
              </Button>
            </div>
          )}

          {cancelAllowed && data.can_cancel && (
            <div className="mt-8 flex flex-col gap-3">
              {policy && <p className="font-body-md text-sm text-on-surface-variant">{policy}</p>}
              <Button
                to={`/${slug}/account/bookings/${data.id}/cancel`}
                variant="outlined"
                size="lg"
                className="border-error text-error hover:bg-error/5"
              >
                Cancel appointment
              </Button>
            </div>
          )}

          {/* Only a not-yet-started booking can be "too close to cancel"; a visit under
              way or finished simply has nothing left to cancel. */}
          {cancelAllowed && !data.can_cancel && SCHEDULED.includes(data.status) && (
            <p className="mt-8 flex items-start gap-2 font-body-md text-sm text-on-surface-variant">
              <span className="material-symbols-outlined text-[18px]">info</span>
              This appointment is too close to its start time to cancel online
              {policy ? `. ${policy}` : '.'} Please call the salon if you need to change it.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
